import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import ScrollToTop from "@/components/base/ScrollToTop";
import Seo from "@/components/base/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionHeading from "@/components/base/SectionHeading";
import TestimonialWall from "@/pages/home/components/TestimonialWall";
import FaqSection from "@/pages/home/components/FaqSection";
import ContactForm from "./components/ContactForm";
import { site } from "@/config/site";

const ORIGIN = "https://sjlm888.cn";

const principles = [
  {
    icon: "ri-shield-check-line",
    title: "来源可核实",
    desc: "每条内容都基于可查证的公开信息整理，不臆测、不编造，也不把传闻当结论。",
  },
  {
    icon: "ri-scales-3-line",
    title: "表达有边界",
    desc: "涉及个人隐私与未定论的话题，我们保持克制。热闹可以看，分寸不能丢。",
  },
  {
    icon: "ri-quill-pen-line",
    title: "重写而非搬运",
    desc: "所有内容由编辑团队重新撰写组织，把来龙去脉讲清楚，而不是堆砌零散片段。",
  },
  {
    icon: "ri-refresh-line",
    title: "持续更新",
    desc: "每日早晚两次集中更新，热点出现时补充跟进，让今天有什么瓜始终是最新的。",
  },
];

const workflow = [
  { step: "01", title: "选题筛选", desc: "从当天讨论度较高的话题里，筛出真正值得一看的内容方向。" },
  { step: "02", title: "信息核实", desc: "交叉比对多个公开来源，确认基本事实与时间线，剔除存疑部分。" },
  { step: "03", title: "重写整理", desc: "按读者视角重新组织语言，补充背景，让不了解前因的人也能看懂。" },
  { step: "04", title: "分类发布", desc: "归入对应瓜田栏目并计算热度，进入今日新瓜与热瓜榜。" },
];

export default function AboutPage() {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于我们 - 51吃瓜",
      description: site.description,
      url: `${ORIGIN}/about`,
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: site.name,
      url: `${ORIGIN}/`,
      email: site.email,
      slogan: site.tagline,
      description: site.description,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "关于我们", item: `${ORIGIN}/about` },
      ],
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Seo
        title="关于我们 - 51吃瓜的内容原则与团队"
        description="了解51吃瓜的定位、内容原则与工作方式。我们每日整理今日新鲜瓜，坚持来源可核实、表达有边界、重写而非搬运，让吃瓜群众不迷路。"
        keywords="51吃瓜,关于51吃瓜,51吃瓜内容原则,51吃瓜每日新瓜,今日新鲜瓜"
        path="/about"
        jsonLd={jsonLd}
      />
      <SiteHeader />
      <main className="flex-1">
        <section className="w-full px-4 md:px-8 pt-24 md:pt-32 pb-10 bg-background-100">
          <div className="max-w-content mx-auto">
            <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "关于我们" }]} />
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium tracking-[0.2em] text-primary-600 uppercase">
                <span className="w-4 h-px bg-primary-500"></span>
                About Us
              </span>
              <h1 className="mt-3 font-heading font-black text-3xl md:text-5xl text-foreground-950 leading-tight">
                关于 {site.name}
              </h1>
              <p className="mt-5 text-base md:text-lg text-foreground-600 leading-relaxed">
                {site.slogan}
              </p>
            </div>

            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { value: "每日", label: "早晚两次更新" },
                { value: "6 大", label: "瓜田分类栏目" },
                { value: "3 万+", label: "日均阅读人次" },
                { value: "0", label: "未经核实的传闻" },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5"
                >
                  <p className="font-heading font-black text-2xl md:text-3xl text-primary-600">
                    {stat.value}
                  </p>
                  <p className="mt-1 text-xs md:text-sm text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-12 md:py-16">
          <div className="max-w-content mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <SectionHeading
                eyebrow="Our Story"
                title="我们想做的，只是一份好读的新瓜清单"
              />
              <div className="mt-6 space-y-4 text-sm md:text-base leading-relaxed text-foreground-600">
                <p>
                  娱乐话题每天都在发生，但信息往往零散、重复，甚至互相矛盾。51吃瓜想做的事情很简单：把散落各处的热点整理成一条条清晰、好读的内容，让关心这些事的人不必再翻来翻去。
                </p>
                <p>
                  我们不追求最快，也不追求最多。比起抢在别人之前发布，我们更在意这条瓜讲得是否清楚、是否站得住。所以你会看到，有些热点我们选择观望，而不是匆匆跟风。
                </p>
                <p>
                  从明星动态到影视追踪，从综艺看点、音乐现场到时尚风向与海外娱乐，六块瓜田每天更新。你可以按兴趣订阅，也可以只把它当作通勤路上的十分钟。
                </p>
              </div>
              <Link
                to="/rank"
                className="mt-7 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                看看今天的热瓜榜
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="rounded-lg overflow-hidden aspect-[4/3] bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=editorial%20brand%20story%20abstract%20collage%20of%20stacked%20paper%20pages%20and%20warm%20light%2C%20coral%20red%20and%20golden%20amber%20shapes%20on%20warm%20cream%20paper%20background%2C%20stylized%20geometric%20soft%20grain%20minimal%20flat%20illustration%2C%20no%20text&width=1200&height=900&seq=about-brand-2026&orientation=landscape"
                alt="51吃瓜内容整理工作场景插画"
                title="51吃瓜 - 每日整理今日新鲜瓜"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        <section id="principles" className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-100 scroll-mt-24">
          <div className="max-w-content mx-auto">
            <SectionHeading
              eyebrow="Principles"
              title="我们的内容原则"
              desc="热闹之外，我们也想守住一些基本的东西。"
              align="center"
            />
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {principles.map((item) => (
                <div
                  key={item.title}
                  className="rounded-lg border border-background-200 bg-background-50 p-6"
                >
                  <span className="w-12 h-12 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center">
                    <i className={`${item.icon} text-2xl`}></i>
                  </span>
                  <h3 className="mt-4 font-heading font-bold text-lg text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-12 md:py-16">
          <div className="max-w-content mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <SectionHeading
                eyebrow="How We Work"
                title="一条瓜是怎么到你面前的"
                desc="从选题到发布，大致会经历这四个步骤。"
              />
            </div>
            <div className="lg:col-span-8">
              <ol className="space-y-4">
                {workflow.map((item) => (
                  <li
                    key={item.step}
                    className="flex gap-5 rounded-lg border border-background-200 bg-background-50 p-5"
                  >
                    <span className="font-heading font-black text-2xl text-accent-500 shrink-0">
                      {item.step}
                    </span>
                    <div>
                      <h3 className="font-heading font-bold text-base md:text-lg text-foreground-950">
                        {item.title}
                      </h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
                        {item.desc}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <TestimonialWall />

        <section id="contact" className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-100 scroll-mt-24">
          <div className="max-w-content mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <SectionHeading
                eyebrow="Contact"
                title="投稿与合作"
                desc="有想推荐的话题、发现内容问题，或者想聊聊合作，欢迎留言给我们。"
              />
              <div className="mt-6 space-y-3">
                <a
                  href={`mailto:${site.email}`}
                  className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 hover:border-primary-300 transition-colors"
                >
                  <span className="w-11 h-11 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center">
                    <i className="ri-mail-line text-xl"></i>
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-400">邮箱联系</span>
                    <span className="block text-sm text-foreground-900">{site.email}</span>
                  </span>
                </a>
                <Link
                  to="/#subscribe"
                  className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 hover:border-primary-300 transition-colors"
                >
                  <span className="w-11 h-11 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center">
                    <i className="ri-notification-3-line text-xl"></i>
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-400">不想留言？</span>
                    <span className="block text-sm text-foreground-900">
                      直接订阅新瓜早报
                    </span>
                  </span>
                </Link>
              </div>
            </div>
            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </section>

        <div id="faq" className="scroll-mt-24">
          <FaqSection />
        </div>
      </main>
      <SiteFooter />
      <ScrollToTop />
    </div>
  );
}